import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Paper, TableContainer, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { AlertDialog } from "../../components/core/Dialogs/AlertDialog";
import http from "../../api/core/api";
import { apiUrl } from "../../api/core/apiUrl";
import DataGridTable from "../../components/core/DataGridTable/DataGridTable";
import { Loader } from "../../components/core/Loader/Loader";
import { useDeleteDialog } from "../../hooks/useDeleteDialog";
import { dictionary } from "../../utils/core/dictionary";
import { BoardButton } from "../../types/app/boardButton.types";
import { paths } from "../../utils/core/routes";
import { useParams } from "react-router-dom";

interface Props {
  readOnly: boolean;
}

export function BoardButtonList({ readOnly }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const [boardButtonList, setBoardButtonList] = useState<BoardButton[] | null>();

  const fetchBoardButtons = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.BOARD_BUTTON_LIST}?parentId=${id}`,
    });
    setBoardButtonList(response?.data);
  };

  useEffect(() => {
    fetchBoardButtons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRemoveItem = async (idToDelete: number | null) => {
    if (idToDelete === null) return;
    const response = await http.apiDelete({
      url: `${apiUrl.BOARD_BUTTON_DELETE}?id=${idToDelete}`,
    });
    if (response?.status === 204) {
      enqueueSnackbar(dictionary.BOARD_BUTTON_DELETED, {
        variant: "success",
      });
      confirmDeleteAttempt();
      fetchBoardButtons();
    }
  };

  const columns = [
    {
      headerName: dictionary.BOARD_BUTTON_LABEL,
      field: "label",
      width: 200,
    },
    {
      headerName: dictionary.BOARD_BUTTON_URL,
      field: "url",
      flex: 1,
      minWidth: 200,
    },
    {
      headerName: dictionary.BOARD_BUTTON_STYLE,
      field: "style",
      width: 120,
    },
    {
      headerName: dictionary.BOARD_BUTTON_ORDER,
      field: "order",
      width: 90,
    },
    {
      field: " ",
      sortable: false,
      disableColumnMenu: true,
      align: "right",
      width: 120,
      renderCell: (params: any) => {
        return (
          <>
            {!readOnly && (
              <>
                <Tooltip title={dictionary.EDIT}>
                  <Button
                    style={{ minWidth: 30 }}
                    onClick={() => handleOnEdit(params.id)}
                  >
                    <EditIcon />
                  </Button>
                </Tooltip>
                <Tooltip title={dictionary.DELETE}>
                  <Button
                    style={{ minWidth: 30 }}
                    onClick={() => handleOnDelete(params.id)}
                  >
                    <DeleteIcon />
                  </Button>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
  ];
  const handleOnCreate = () =>
    navigate(`${paths.BOARD_DETAIL}/${id}${paths.BOARD_BUTTON_CREATE}`);
  const handleOnEdit = (buttonId: number) =>
    navigate(`${paths.BOARD_DETAIL}/${id}${paths.BOARD_BUTTON_EDIT}/${buttonId}`);
  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    onRemoveItem(idToDelete);
  };
  if (!boardButtonList) return <Loader />;
  return (
    <>
      <TableContainer component={Paper}>
        <DataGridTable
          height={"70vh"}
          data={boardButtonList}
          columns={columns}
          fulltextSearch={true}
          autoPageSize={true}
        />
        {!readOnly && (
          <Tooltip title={dictionary.NEW}>
            <Button onClick={handleOnCreate}>
              <AddIcon />
            </Button>
          </Tooltip>
        )}
      </TableContainer>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={dictionary.DELETE}
        content={dictionary.DELETE_MESSAGE}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
